<template>
  <div v-loading="loading">
    <main-body :invoiceDetail="invoiceDetail" :languageMap="languageMap">
      <!-- 注入DESCRIPTION和AMOUNT -->
      <template #descriptionAndAmount>
        <!-- 退款发票(invoiceType == 2)才显示 -->
        <template v-if="invoiceDetail.invoiceType == 2">
          <p class="mainTitle">
            Refund Of
            <span class="smallDesc">{{ languageMap.refundOf }}</span>
            :
          </p>
        </template>

        <!-- 服务费 -->
        <div class="rowItem" v-if="serviceChargeList.length > 0">
          <p class="secondTitle">
            Service Charge
            <span class="smallDesc">{{ languageMap.serviceCharge }}</span>
          </p>
          <div class="feeItem" v-for="(item, index) in serviceChargeList" :key="`serviceCharge-${index}`">
            <p class="leftDesc">{{ item.title }} {{ item.exRateDesc }}</p>
            <p class="rightAmount">{{ item.amount }}</p>
          </div>
        </div>

        <!-- 第三方费用 -->
        <div class="rowItem" v-if="thirdPartyFeeList.length > 0">
          <p class="secondTitle">
            Third Party Fee
            <span class="smallDesc">{{ languageMap.thirdPartyFee }}</span>
          </p>
          <div class="feeItem" v-for="(item, index) in thirdPartyFeeList" :key="`thirdPartyFee-${index}`">
            <p class="leftDesc">{{ item.title }} {{ item.exRateDesc }}</p>
            <p class="rightAmount">{{ item.amount }}</p>
          </div>
        </div>

        <!-- 其他费用 -->
        <div class="rowItem" v-if="otherFeeList.length > 0">
          <p class="secondTitle">
            Other Fee
            <span class="smallDesc">{{ languageMap.otherFee }}</span>
          </p>
          <div class="feeItem" v-for="(item, index) in otherFeeList" :key="`otherFee-${index}`">
            <p class="leftDesc">{{ item.title }} {{ item.exRateDesc }}</p>
            <p class="rightAmount">{{ item.amount }}</p>
          </div>
        </div>

        <!-- 扣除项，排除Quotation (V) 并且 退款发票(invoiceType == 2)才显示 -->
        <template v-if="invoiceDetail.businessType != 'ADJUSTMENT' && invoiceDetail.invoiceType == 2">
          <div class="rowItem" v-if="$isNumberLike(invoiceDetail.deductionHandlingFee) || $isNumberLike(invoiceDetail.deductionAcraFee)">
            <p class="mainTitle">
              Less
              <span class="smallDesc">{{ languageMap.less }}</span>
              :
            </p>
            <div class="feeItem" v-if="$isNumberLike(invoiceDetail.deductionHandlingFee)">
              <p class="leftDesc">
                - Handling Fee
                <span class="smallDesc">{{ languageMap.handlingFee }}</span>
              </p>
              <p class="rightAmount">({{ priceCurrencyFormat(invoiceDetail.deductionHandlingFee, invoiceDetail.billingCurrency) }})</p>
            </div>
            <div class="feeItem" v-if="$isNumberLike(invoiceDetail.deductionAcraFee)">
              <p class="leftDesc">
                - Gov Fee
                <span class="smallDesc">{{ languageMap.govFee }}</span>
              </p>
              <p class="rightAmount">({{ priceCurrencyFormat(invoiceDetail.deductionAcraFee, invoiceDetail.billingCurrency) }})</p>
            </div>
          </div>
        </template>
      </template>
    </main-body>
  </div>
</template>

<script>
import { getAction } from "@/api/common";
import { isNumberLike } from "@/utils/common";
import { addition } from "@/utils/count";
import formatMixin from "@/mixins/formatMixin";
import priceCountMixin from "@/mixins/priceCountMixin";
import MainBody from "./components/MainBody.vue";
import { mapGetters } from "vuex";
import { buildInvoiceDetail } from "@/utils/invoicePreviewMockData";
export default {
  name: "Invoice",
  mixins: [formatMixin, priceCountMixin],
  components: {
    MainBody,
  },
  data() {
    return {
      invoiceNo: null,
      previewId: null, // 预览用的模板ID
      invoiceDetail: {}, // 发票详情
      languageMap: {}, // 国家对应的语言对象
      loading: false,
    };
  },
  computed: {
    ...mapGetters("constant", ["getInvoiceLanguageMap"]),
    // 服务费列表
    serviceChargeList() {
      const billingCurrency = this.invoiceDetail?.billingCurrency;
      const productList = this.invoiceDetail?.productList || [];
      // 取productList数组第一位的productType，作为类型判定
      const productFirst = productList[0];
      const productType = productFirst?.productType;
      if (!productType) return [];
      if (productType == "package") {
        // 套餐
        // 套餐只存在一条数据；套餐币种和开票币种是相同的，不存在汇率，所以exRateDesc设为空；
        const title = productFirst?.packageInfo?.packageName || "";
        const exRateDesc = "";
        let serviceFee = null;
        if (this.invoiceDetail.invoiceType == 1) {
          // 普通发票，应税金额(taxableAmount)就是服务费
          serviceFee = this.invoiceDetail.taxableAmount;
        } else if (this.invoiceDetail.invoiceType == 2) {
          // 退款发票，服务费 = 应税金额(taxableAmount) + 扣除的手续费(deductionHandlingFee)
          serviceFee = addition(this.invoiceDetail.taxableAmount, this.invoiceDetail.deductionHandlingFee);
        }
        const amount = this.priceCurrencyFormat(serviceFee, billingCurrency);
        return [{ title, exRateDesc, amount }];
      } else if (productType == "service") {
        // 自定义套餐、Quotation定制化服务
        // 自定义套餐、Quotation币种和开票币种是相同的，不存在汇率，所以exRateDesc设为空
        const list = productList.map(item => ({
          title: item.serviceInfo?.description || "",
          exRateDesc: "",
          amount: this.priceCurrencyFormat(item.serviceInfo?.serviceFee, billingCurrency),
        }));
        return list;
      } else if (productType == "invoiceLine") {
        // Quotation(V)
        const filterList = productList.filter(item => item?.invoiceLine?.category == 1);
        const list = filterList.map(item => ({
          title: item.invoiceLine?.description || "",
          exRateDesc:
            isNumberLike(item.invoiceLine?.originalAmount) && isNumberLike(item.invoiceLine?.exchangeRate)
              ? `(${this.priceCurrencyFormat(item.invoiceLine.originalAmount, item.invoiceLine?.originalCurrency)} @ex rate of ${Number(item.invoiceLine.exchangeRate)})`
              : "",
          amount: this.priceCurrencyFormat(item.invoiceLine?.billingAmount, billingCurrency),
        }));
        return list;
      } else {
        // 不存在其他类型，直接返回空数组
        return [];
      }
    },
    // 第三方费用列表
    thirdPartyFeeList() {
      const billingCurrency = this.invoiceDetail?.billingCurrency;
      const productList = this.invoiceDetail?.productList || [];
      // 取productList数组第一位的productType，作为类型判定
      const productFirst = productList[0];
      const productType = productFirst?.productType;
      if (!productType) return [];
      if (productType == "package") {
        // 套餐
        // 套餐只存在一条数据
        const serviceList = productFirst?.packageInfo?.serviceList || [];
        // 合并相同项, mergeSameAttrList是mixin中的方法
        const mergeList = this.mergeSameAttrList(serviceList, "thirdPartyFeeDescription", "thirdPartyFee");
        // 套餐币种和开票币种是相同的，不存在汇率，所以exRateDesc设为空
        const list = mergeList.map(item => ({
          title: item.thirdPartyFeeDescription || "",
          exRateDesc: "",
          amount: this.priceCurrencyFormat(item.thirdPartyFee, billingCurrency),
        }));
        return list;
      } else if (productType == "service") {
        // 自定义套餐、Quotation定制化服务
        const serviceInfoList = productList.map(item => item.serviceInfo || {});
        // 合并相同项, mergeSameAttrList是mixin中的方法
        const mergeList = this.mergeSameAttrList(serviceInfoList, "thirdPartyFeeDescription", "thirdPartyFee");
        // 自定义套餐、Quotation币种和开票币种是相同的，不存在汇率，所以exRateDesc设为空
        const list = mergeList.map(item => ({
          title: item.thirdPartyFeeDescription || "",
          exRateDesc: "",
          amount: this.priceCurrencyFormat(item.thirdPartyFee, billingCurrency),
        }));
        return list;
      } else if (productType == "invoiceLine") {
        // Quotation(V)
        const filterList = productList.filter(item => item?.invoiceLine?.category == 2);
        const list = filterList.map(item => ({
          title: item.invoiceLine?.description || "",
          exRateDesc:
            isNumberLike(item.invoiceLine?.originalAmount) && isNumberLike(item.invoiceLine?.exchangeRate)
              ? `(${this.priceCurrencyFormat(item.invoiceLine.originalAmount, item.invoiceLine?.originalCurrency)} @ex rate of ${Number(item.invoiceLine.exchangeRate)})`
              : "",
          amount: this.priceCurrencyFormat(item.invoiceLine?.billingAmount, billingCurrency),
        }));
        return list;
      } else {
        // 不存在其他类型，直接返回空数组
        return [];
      }
    },
    // 其他费用列表
    otherFeeList() {
      const billingCurrency = this.invoiceDetail?.billingCurrency;
      const productList = this.invoiceDetail?.productList || [];
      // 取productList数组第一位的productType，作为类型判定
      const productFirst = productList[0];
      const productType = productFirst?.productType;
      if (!productType) return [];
      if (productType == "package") {
        // 套餐
        // 套餐只存在一条数据
        const serviceList = productFirst?.packageInfo?.serviceList || [];
        // 合并相同项, mergeSameAttrList是mixin中的方法
        const mergeList = this.mergeSameAttrList(serviceList, "otherFeeDescription", "otherFee");
        // 套餐币种和开票币种是相同的，不存在汇率，所以exRateDesc设为空
        const list = mergeList.map(item => ({
          title: item.otherFeeDescription || "",
          exRateDesc: "",
          amount: this.priceCurrencyFormat(item.otherFee, billingCurrency),
        }));
        return list;
      } else if (productType == "service") {
        // 自定义套餐、Quotation定制化服务
        const serviceInfoList = productList.map(item => item.serviceInfo || {});
        // 合并相同项, mergeSameAttrList是mixin中的方法
        const mergeList = this.mergeSameAttrList(serviceInfoList, "otherFeeDescription", "otherFee");
        // 自定义套餐、Quotation币种和开票币种是相同的，不存在汇率，所以exRateDesc设为空
        const list = mergeList.map(item => ({
          title: item.otherFeeDescription || "",
          exRateDesc: "",
          amount: this.priceCurrencyFormat(item.otherFee, billingCurrency),
        }));
        return list;
      } else if (productType == "invoiceLine") {
        // Quotation(V)
        const filterList = productList.filter(item => item?.invoiceLine?.category == 3);
        const list = filterList.map(item => ({
          title: item.invoiceLine?.description || "",
          exRateDesc:
            isNumberLike(item.invoiceLine?.originalAmount) && isNumberLike(item.invoiceLine?.exchangeRate)
              ? `(${this.priceCurrencyFormat(item.invoiceLine.originalAmount, item.invoiceLine?.originalCurrency)} @ex rate of ${Number(item.invoiceLine.exchangeRate)})`
              : "",
          amount: this.priceCurrencyFormat(item.invoiceLine?.billingAmount, billingCurrency),
        }));
        return list;
      } else {
        // 不存在其他类型，直接返回空数组
        return [];
      }
    },
  },
  methods: {
    // 获取发票详情
    getInvoiceList() {
      this.loading = true;
      getAction("/customerInvoice/getByInvoiceNo", {
        invoiceNo: this.invoiceNo,
      })
        .then(res => {
          const resData = res.data || {};
          this.invoiceDetail = resData;
          const { country, languageOption } = resData.invoiceTemplate || {};
          this.languageMap = this.getInvoiceLanguageMap(country, languageOption);
        })
        .catch(err => {})
        .finally(() => {
          this.loading = false;

          // 等待 Vue 更新 DOM
          this.$nextTick(() => {
            // 拿到子页面里所有的图片标签
            const images = document.querySelectorAll("img");
            const promises = Array.from(images).map(img => {
              if (img.complete) return Promise.resolve(); // 如果图片已经缓存好了，直接通过
              return new Promise(resolve => {
                img.onload = resolve; // 否则等待图片加载完
                img.onerror = resolve; // 报错也通过，防止死锁
              });
            });
            // 所有的图片都下载完、撑开高度后，再通知父页面
            Promise.all(promises).then(() => {
              window.parent.postMessage(
                {
                  type: "INVOICE_DATA_RENDERED",
                  invoiceNo: this.invoiceNo,
                },
                window.location.origin,
              );
            });
          });
        });
    },
    // 获取预览发票模版详情
    getInvoiceTemplatePreview() {
      this.loading = true;
      getAction(`/invoiceTemplate/${this.previewId}`)
        .then(res => {
          const resData = res.data || {};
          this.invoiceDetail = buildInvoiceDetail(resData);
          const { country, languageOption } = resData;
          this.languageMap = this.getInvoiceLanguageMap(country, languageOption);
        })
        .catch(err => {})
        .finally(() => {
          this.loading = false;
        });
    },
  },
  created() {
    const query = this.$route.query;
    // 检查url是否存在参数(id)
    if (query.invoiceNo) {
      this.invoiceNo = query.invoiceNo;
      // 获取发票详情
      this.getInvoiceList();
    } else if (query.previewId) {
      // 【新增】获取预览发票模版详情
      this.previewId = query.previewId;
      this.getInvoiceTemplatePreview();
    } else {
      // 缺少参数
      this.$message({
        message: this.$t("common.error.scarce"),
        type: "error",
        duration: 1000,
        onClose: () => {
          // this.$router.replace("/");
        },
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.smallDesc {
  font-size: 10px;
  color: #555;
  font-weight: normal !important;
  font-style: italic !important;
}
</style>
